import type { OwaDate } from 'owa-datetime';
import FreeBusyType from '../schema/FreeBusyType';
import { getPageIndex, getRecordIndex, getPageRange } from './pageUtils';
import { tryGetFreeBusy } from './tryGetFreeBusy';
import { scaleRangeAsCacheSize } from './scaleRangeAsCacheSize';
import { getSessionFilterEvent } from '../selectors/sessionSelectors';
import { isBefore, isAfter } from 'owa-datetime';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';

/**
 * Gets an array of free busy information for an attendee between the start and end time
 * @param sessionId Id for this free/busy cache session
 * @param attendee The SMTP address for which free busy is requested
 * @param start The start time for which to retrieve free busy
 * @param end The end time for which to retrieve free busy
 */
export function getDetailedFreeBusy(
    sessionId: string,
    attendee: string,
    start: OwaDate,
    end: OwaDate
): FreeBusyType[] {
    const updatedEnd = scaleRangeAsCacheSize(start, end);
    const startPageIndex = getPageIndex(start);
    const endPageIndex = getPageIndex(updatedEnd);
    const startRecordIndex = Math.floor(getRecordIndex(start));
    let endRecordIndex = Math.ceil(getRecordIndex(updatedEnd));

    // Special handling for <15 minute events.
    if (startPageIndex == endPageIndex && startRecordIndex == endRecordIndex) {
        endRecordIndex++;
    }

    const mergedFreeBusy: FreeBusyType[] = [];

    for (let index = startPageIndex; index <= endPageIndex; index++) {
        const pageObservable = tryGetFreeBusy(sessionId, attendee, index)?.mergedFreeBusy;

        if (pageObservable) {
            // Create copy of the array to avoid modifying the observable.
            let page = pageObservable.slice();

            page = tryApplyEventFilter(page, sessionId, attendee, index);

            if (index == endPageIndex) {
                page = page.slice(0, endRecordIndex);
            }
            if (index == startPageIndex) {
                page = page.slice(startRecordIndex);
            }
            mergedFreeBusy.push(...page);
        }
    }

    return mergedFreeBusy;
}

/*
 * Hypothesis: Organizing or accepting a meeting request is an implicit commitment
 * to be available for the duration of the event. Therefore, marking such time as
 * free is arguably valid.
 *
 * Drawback: This code will also wipe out other events occurring in the same time
 * period. More nuanced filtering would be possible by either:
 *
 * a) Performing the filtering in the server, where complete data is available for
 * every attendee,
 *
 * b) Obtaining an ItemId for each of the items of all the attendees (possibly an
 * information leak) or,
 *
 * c) As a last resort, best effort filtering might also be possible. This would
 * attempt to match based on either an ItemId or times and FreeBusyType depending on
 * access level. After the matching and filtering, the merged free busy can be
 * recomputed from the remaining items.
 */
function tryApplyEventFilter(
    pageFreeBusy: FreeBusyType[],
    sessionId: string,
    attendee: string,
    pageIndex: number
): FreeBusyType[] {
    const filter = getSessionFilterEvent(sessionId);

    if (!filter) {
        return pageFreeBusy;
    }

    const pageRange = getPageRange(pageIndex);
    const isConflict =
        isAfter(filter.End, pageRange.start) && isBefore(filter.Start, pageRange.end);

    if (!isConflict) {
        return pageFreeBusy;
    }

    if (!shouldFilterForAttendee(attendee, filter)) {
        return pageFreeBusy;
    }

    const filterStart = isBefore(filter.Start, pageRange.start) ? pageRange.start : filter.Start;
    const filterEnd = isAfter(filter.End, pageRange.end) ? pageRange.end : filter.End;
    const startRecordIndex = getRecordIndex(filterStart);
    const endRecordIndex = getRecordIndex(filterEnd);

    for (let i = startRecordIndex; i < endRecordIndex; i++) {
        pageFreeBusy[i] = FreeBusyType.Free;
    }

    return pageFreeBusy;
}

// Only filter for the organizer and attendees who have accepted the meeting
function shouldFilterForAttendee(attendee: string, event: CalendarEvent) {
    if (event.Organizer?.Mailbox?.EmailAddress == attendee) {
        return true;
    }

    const attendees = [
        ...event.RequiredAttendees,
        ...event.OptionalAttendees,
        ...event.Resources,
    ].filter(eventAttendee => eventAttendee?.Mailbox?.EmailAddress == attendee);

    if (attendees.length) {
        return attendees[0].ResponseType == 'Accept';
    }

    return false;
}
